import React, { Component } from 'react';
import { connect } from 'react-redux';
import { ConnectedProps } from 'react-redux/es/exports';
import CustomTable, { CustomColumnProps } from 'components/custom/CustomTable';
import CustomPaginator, { CustomPaginatorProps } from 'components/custom/CustomPaginator';
import { FormicaProps, withFormica } from '../../helpers/withFormica';
import { dispatch, RootState } from '../../redux/store';
import { TransactionData, TransactionJsonModel, TransactionResult, TransactionVariableJsonModel } from '../../domain/stream/transaction/TransactionDomains';
import { InstanceContainer } from '../../config/binding/InstanceBinding';
import Injectables from '../../constant/Injectables';
import { query, repository, subscribe } from '@trlogic/client-js';
import CollectionNames from '../../constant/CollectionNames';
import { DataTableSortMeta } from 'primereact/datatable';
import { ComponentState, setSelectedItems } from '../../redux/actions/ComponentActions';
import TransactionFailureModalComponent from 'components/investigation/case/widget/TransactionFailureModalComponent';
import CustomTableContentWrapper from '../custom/CustomTableContentWrapper';
import { Dialog } from 'primereact/dialog';
import { Tree } from 'primereact/tree';
import TreeNode from 'primereact/treenode';
import { User, UserSystemAttributes } from '../../domain/rbac/RbacDomains';
import TransactionResultModalComponent from 'components/investigation/case/widget/TransactionResultModalComponent';
import { CaseState } from '../../redux/reducers/CaseReducer';
import { changeCaseColumns, changeListDataLoading } from '../../redux/actions/CaseActions';
import ComponentName from '../../constant/ComponentName';
import { setAttribute } from '../../redux/actions/UserActions';
import ValueFactory from '../../util/ValueFactory';
import CustomSkeleton from '../custom/CustomSkeleton';
import { PageState } from '../../redux/reducers/PageReducer';
import QueryBuilder = query.QueryBuilder;
import eq = query.eq;
import Order = query.Order;
import Computation = subscribe.Computation;
import LanguageUtil from '../../util/LanguageUtil';
import DataType from 'constant/DataType';
import { ColumnProps } from 'primereact/column';
import { generateUUID } from 'util/Generator';
import EmptyListComponent from 'components/custom/empty-components/EmptyListComponent';
import DateFormatUtil from '../../util/DateFormatUtil';
import { DateVariableProperties } from '../../domain/datatype/VariablePropertiesDomains';

class SandboxTransactionListComponent extends Component<Props, State> {
  private repository: repository.ReadOnlyQueryableRepository = InstanceContainer.get(Injectables.REPOSITORY);
  private userSystemAttributeSubscription: subscribe.Subscription | undefined;
  private computation: Computation | undefined;
  public state = new State();
  render() {
    const { currentPage, rows, transactionFailureVisible, transactionFailures, structureVisible, structureData, transactionResultVisible, transactionResult } =
      this.state;
    const {
      transactions,
      paginationFixed,
      total,
      multiple,
      loading,
      component: { selectedComponentIds },
      onSortChange,
      case: { columns },
    } = this.props;
    const defaultColumns = columns['default'] ?? [];
    const isEmpty = transactions.length == 0 && defaultColumns.length > 0 && !loading;
    return (
      <div className='w-full h-full flex flex-col overflow-y-scroll max-h-[calc(100vh-400px)] 2xl:max-h-[calc(100vh-400px)] xl:max-h-[calc(100vh-380px)] '>
        {isEmpty ? (
          <div className={'h-[100vh] w-full flex items-center justify-center'}>
            <EmptyListComponent />
          </div>
        ) : loading ? (
          <CustomSkeleton />
        ) : (
          <div>
            <div className={'flex justify-between bg-white items-center w-full'}>
              <CustomTable
                columns={defaultColumns}
                rowClassName={this.onRowClassName}
                onSortChange={onSortChange}
                value={transactions}
                selectionMode={multiple ? 'checkbox' : undefined}
                onSelectionChange={(e) => dispatch(setSelectedItems(e.value))}
                selection={selectedComponentIds}
                reorderableColumns
                multiSortMeta={this.getOrder()}
              />
            </div>
            <CustomPaginator
              currentPage={currentPage}
              currentRecords={transactions.length}
              key='sandbox-transaction-activity'
              rows={rows}
              totalRecords={total > 10_000 ? 10_000 : total}
              loading={loading}
              fixed={paginationFixed}
              rowsPerPageOptions={[5, 10, 20, 50, 100]}
              onPageChange={this.onPageChange}
            />
          </div>
        )}
        <TransactionFailureModalComponent
          failures={transactionFailures}
          visible={transactionFailureVisible}
          onClose={() => this.setState({ transactionFailures: [], transactionFailureVisible: false })}
        />
        {transactionResult && (
          <TransactionResultModalComponent
            result={transactionResult}
            visible={transactionResultVisible}
            onClose={() => this.setState({ transactionResult: undefined, transactionResultVisible: false })}
          />
        )}
        <Dialog
          className={'h-1/2 w-1/3'}
          headerClassName={'!pb-0'}
          closable={false}
          header={<span className={'text-[15px] text-normal'}>Structure Items</span>}
          dismissableMask
          closeOnEscape
          visible={structureVisible}
          onHide={() => this.setState({ structureVisible: !structureVisible })}
        >
          <Tree value={structureData} className={'border-none h-full w-full text-[13px] p-0 pt-2'} />
        </Dialog>
      </div>
    );
  }

  componentDidUpdate(prevProps: Props) {
    if (prevProps.selectedTransaction !== this.props.selectedTransaction) {
      const { user, onPageChange } = this.props;
      const attrQuery = QueryBuilder.select().from(CollectionNames.USER_SYSTEM_ATTRIBUTES).where(eq('username', user.username)).build();

      this.repository.find<UserSystemAttributes>(attrQuery).then((attr) => {
        this.prepareColumns(attr ?? ValueFactory.initializeUserAttribute());
      });
      onPageChange(1, this.props.rows);
    }
  }

  public async componentDidMount() {
    const { currentPage, rows, changeListDataLoading } = this.props;
    changeListDataLoading(true);
    if (currentPage != undefined) {
      this.setState({ currentPage });
    }
    if (rows != undefined) {
      this.setState({ rows });
    }
    this.startTracker();
  }

  private startTracker = () => {
    const { user } = this.props;
    const attrQuery = QueryBuilder.select().from(CollectionNames.USER_SYSTEM_ATTRIBUTES).where(eq('username', user.username)).build();
    this.userSystemAttributeSubscription = subscribe.Formica.subscribe(CollectionNames.USER_SYSTEM_ATTRIBUTES, attrQuery);
    this.computation = subscribe.Tracker.autorun(this.tracker, [this.userSystemAttributeSubscription]);
  };

  private tracker = async () => {
    const { user, setAttribute } = this.props;
    if (this.userSystemAttributeSubscription?.ready()) {
      const attrQuery = QueryBuilder.select().from(CollectionNames.USER_SYSTEM_ATTRIBUTES).where(eq('id', user.username)).build();
      const attr = (await this.repository.find<UserSystemAttributes>(attrQuery)) ?? ValueFactory.initializeUserAttribute();
      setAttribute(attr);
      this.prepareColumns(attr);
    }
  };

  public componentWillUnmount() {
    this.userSystemAttributeSubscription?.stop();
    this.computation?.stop();
  }

  private prepareColumns = async (attribute: UserSystemAttributes) => {
    const { user } = this.props;
    const riskType = user.activeRiskType;
    const columns: CustomColumnProps[] = [
      {
        field: 'id.keyword',
        sortField: 'id.keyword',
        header: LanguageUtil.get({ id: 'pages.case.table.id', defaultMessage: 'ID' }),
        alignHeader: 'left',
        className: 'h-[44px] 2xl:h-[52px]',
        copyable: true,
        onCopy: (e: TransactionData) => {
          return e.id;
        },
        body: (record: TransactionData) => {
          return (
            <div
              className={`flex w-full text-xs 2xl:text-md items-start text-f-black-3 justify-start relative group`}
              data-testid={`${ComponentName.CASE}_id_${record.id}`}
            >
              <CustomTableContentWrapper clipSize={20} text={record.id} />
            </div>
          );
        },
      },
      {
        field: 'createdAt',
        header: LanguageUtil.get({ id: 'pages.case.table.createdAt', defaultMessage: 'Created At' }),
        alignHeader: 'left',
        columnKey: 'createdAt',
        className: 'min-w-[100px]',
        copyable: true,
        onCopy: (e: TransactionData) => {
          return DateFormatUtil.formatEpochForDisplay(e.createdAt);
        },
        body: (record: TransactionData) => {
          return (
            <div className={'flex items-start text-f-black-3 text-xs 2xl:text-md'}>
              <CustomTableContentWrapper
                text={DateFormatUtil.formatEpochForDisplay(record.createdAt)}
                tooltipText={DateFormatUtil.formatForTooltip(record.createdAt, 'epochMillis')}
                data-testid={`${ComponentName.CASE}_createdAt_${record.id}`}
              />
            </div>
          );
        },
      },
    ];
    columns.splice(8, 0, {
      field: `result.${riskType}.metadata.code.keyword`,
      sortField: `result.${riskType}.metadata.code.keyword`,
      header: LanguageUtil.get({ id: 'pages.case.table.reason', defaultMessage: 'Reason' }),
      alignHeader: 'left',
      columnKey: `result.${riskType}.metadata.code.keyword`,
      className: 'min-w-[200px]',
      filterable: true,
      body: (record: TransactionData) => {
        const result = record.result[user.activeRiskType];
        return (
          <div
            className={`flex flex-col justify-center items-start text-xs 2xl:text-md rounded-[4px] min-w-[86px] max-w-[150px] h-[22px]`}
            data-testid={`${ComponentName.CASE}_reason_${record.id}`}
          >
            {result?.metadata?.code || 'N/A'}
          </div>
        );
      },
    });
    const transactionFields = await this.prepareTransactionFields();
    columns.push(...transactionFields);
    this.props.changeListDataLoading(false);
    this.props.case.columns['default'] = columns;
    this.props.changeCaseColumns(this.props.case.columns);
    columns.push({
      field: 'response',
      header: LanguageUtil.get({ id: 'pages.case.table.response', defaultMessage: 'Response' }),
      sortable: false,
      alignHeader: 'center',
      columnKey: 'response',
      className: 'min-w-[100]',
      body: (record: TransactionData) => {
        return (
          <div className={'flex flex-col justify-center items-center min-w-[86px] max-w-[150px] text-xs 2xl:text-md'}>
            <button
              onClick={() => this.setState({ transactionResult: record.result[user.activeRiskType], transactionResultVisible: true })}
              className={'text-[#458EF7]'}
              data-testid={`${ComponentName.CASE}_response_${record.id}`}
            >
              {LanguageUtil.get({ id: 'pages.case.table.showResponse', defaultMessage: 'Show Response' })}
            </button>
          </div>
        );
      },
    });
    columns.push({
      field: 'failures',
      header: LanguageUtil.get({ id: 'pages.case.table.failures', defaultMessage: 'Failures' }),
      sortable: false,
      alignHeader: 'center',
      columnKey: 'failures',
      className: 'min-w-[100]',
      body: (record: TransactionData) => {
        return (
          <div className={'flex flex-col justify-center items-center min-w-[86px] max-w-[150px] text-f-black-3 text-xs 2xl:text-md'}>
            {record.result.failures.length == 0 ? (
              'N/A'
            ) : (
              <button
                onClick={() => this.setState({ transactionFailures: record.result.failures, transactionFailureVisible: true })}
                data-testid={`${ComponentName.CASE}_failures_${record.id}`}
              >
                Show
                {LanguageUtil.get({ id: 'pages.case.table.failures', defaultMessage: 'Failures' })}
              </button>
            )}
          </div>
        );
      },
    });
  };

  private prepareTransactionFields = async (): Promise<ColumnProps[]> => {
    const { selectedTransaction } = this.props;
    const transactionQuery = QueryBuilder.select().from(CollectionNames.TRANSACTIONS).where(eq('id', selectedTransaction?.name)).build();
    const transaction = await this.repository.find<TransactionJsonModel>(transactionQuery);
    if (transaction == undefined) return [];
    return transaction.variables.map((v) => ({
      field: v.description && v.description.length > 0 ? v.description : v.name,
      sortField: `variables.${v.name}${v.properties.dataType == DataType.STRING ? '.keyword' : ''}`,
      header: v.description && v.description.length > 0 ? v.description : v.name,
      alignHeader: 'left',
      columnKey: `variables.${v.name}`,
      className: `${v.properties.dataType == DataType.STRING ? 'min-w-[200px]' : 'min-w-[100px]'}`,
      copyable: true,
      filterable: v.properties.dataType != 'Array',
      onCopy: (record: TransactionData) => {
        const value = record.variables?.[v.name];
        if (value == undefined) return 'N/A';
        switch (v.properties.dataType) {
          case DataType.STRING:
            return value;
          case DataType.NUMBER:
            return typeof value == 'number' ? value.toFixed(2) : value;
          case DataType.DATE:
            return DateFormatUtil.formatForTable(value, (v.properties as DateVariableProperties).format);
          case DataType.BOOLEAN:
            return value ? 'true' : 'false';
          case DataType.OBJECT:
          case DataType.STRUCTURE:
          case DataType.ARRAY:
            return JSON.stringify(value);
          default:
            return value;
        }
      },
      body: (record: TransactionData) => {
        return <div data-testid={`${ComponentName.CASE}_variable_${v.name}_${record.id}`}>{this.getTransactionFieldBody(record, v)}</div>;
      },
    }));
  };

  private getTransactionFieldBody = (record: TransactionData, variable: TransactionVariableJsonModel): any => {
    if (record.variables == undefined) return;
    const value = record.variables[variable.name];
    if (value == undefined) return 'N/A';
    switch (variable.properties.dataType) {
      case DataType.STRING:
        if (value.length == 0) return 'N/A';
        return <CustomTableContentWrapper text={value} clipSize={30} />;
      case DataType.NUMBER: {
        if (typeof value == 'number') return value.toFixed(2);
        if (typeof value == 'string') {
          try {
            const parsed = Number(value);
            if (isNaN(parsed)) {
              return;
            }
            return parsed?.toFixed(2);
          } catch (e) {
            return value;
          }
        }
        break;
      }
      case DataType.DATE:
        return (
          <CustomTableContentWrapper
            text={DateFormatUtil.formatForTable(value, (variable.properties as DateVariableProperties).format)}
            tooltipText={DateFormatUtil.formatForTooltip(value, (variable.properties as DateVariableProperties).format)}
            clipSize={50}
          />
        );
      case DataType.BOOLEAN:
        return value || value == 'true' ? 'true' : 'false';
      case DataType.OBJECT:
      case DataType.STRUCTURE:
      case DataType.ARRAY:
        return (
          <a
            className={'text-[#458EF7] text-xs 2xl:text-md'}
            onClick={() => this.setState({ structureVisible: true, structureData: this.getStructureData(value) })}
          >
            Items
          </a>
        );
      default:
        return value;
    }
  };

  private getStructureData = (data: any): TreeNode[] => {
    let result: TreeNode[] = [];
    for (const key in data) {
      result = result.concat(this.convertItem(data[key], key));
    }
    return result;
  };

  private convertItem(item: any, key: any): TreeNode[] {
    let newItem: TreeNode = {
      key: generateUUID(),
      label: key,
      children: [],
    };
    if (Array.isArray(item)) {
      item.forEach((subItem, index) => {
        const subKey = `${index}`;
        newItem.children = newItem?.children?.concat(this.convertItem(subItem, subKey));
      });
    } else if (typeof item === 'object') {
      for (const subKey in item) {
        newItem.children = newItem?.children?.concat(this.convertItem(item[subKey], subKey));
      }
    } else {
      newItem.label = `${key}: ${item}`;
    }
    return [newItem];
  }

  private onPageChange = (event: CustomPaginatorProps) => {
    const { onPageChange } = this.props;
    this.setState({ currentPage: event.page, rows: event.rows });
    onPageChange(event.page, event.rows);
  };

  private onRowClassName = (record: TransactionData) => {
    const hasError = record.result.failures.length > 0;
    return `2xl:!h-[52px] 2xl:!max-h-[52px] !h-[30px] !max-h-[48px]  ${hasError ? '!bg-[#FFF7F7]' : 'bg-white'}`;
  };

  private getOrder = (): DataTableSortMeta[] => {
    const { sort } = this.props;
    return sort.map((s) => {
      return {
        field: s.field,
        order: s.operator == 'ASC' ? 1 : -1,
      } as DataTableSortMeta;
    });
  };
}

interface Props extends FormicaProps, ReduxProps {
  transactions: TransactionData[];
  paginationFixed: boolean;
  onPageChange: (page: number, rows: number) => void;
  onSortChange?: (sort: Order[]) => void;
  total: number;
  rows: number;
  currentPage: number;
  selectedTransaction: TransactionJsonModel | undefined;
  loading?: boolean;
  multiple?: boolean;
  sort: Order[];
}

const mapStateToProps = (state: RootState) => ({
  user: state.auth.user as User,
  systemAttributes: state.auth.attributes as UserSystemAttributes,
  component: state.component as ComponentState,
  case: state.case as CaseState,
  page: state.page as PageState,
});

const connector = connect(mapStateToProps, { changeCaseColumns, setAttribute, changeListDataLoading });
type ReduxProps = ConnectedProps<typeof connector>;

class State {
  rows = 10;
  currentPage = 1;
  transactionFailures: any[] = [];
  transactionFailureVisible: boolean = false;
  transactionResult: TransactionResult | undefined;
  transactionResultVisible: boolean = false;
  structureVisible: boolean = false;
  structureData: TreeNode[] = [];
}

export default withFormica(connector(SandboxTransactionListComponent));
